import type { ModeId, ScreenId } from './state';
/** Navigation maps — verbatim from the reference (script lines 43–106). */
export interface ModeMeta {
  label: string;
  icon: string;
  ac: string;
  dim: string;
  bd: string;
}
export const MODES: Record<ModeId, ModeMeta> = {
  prepare: {
    label: 'Prepare',
    icon: 'ph-sun',
    ac: 'var(--amb)',
    dim: 'color-mix(in srgb, var(--amb) 10%, transparent)',
    bd: 'color-mix(in srgb, var(--amb) 40%, transparent)',
  },
  execute: {
    label: 'Execute',
    icon: 'ph-arrows-left-right',
    ac: 'var(--pos)',
    dim: 'color-mix(in srgb, var(--pos) 10%, transparent)',
    bd: 'color-mix(in srgb, var(--pos) 40%, transparent)',
  },
  analyze: {
    label: 'Analyze',
    icon: 'ph-chart-bar',
    ac: 'var(--vio)',
    dim: 'color-mix(in srgb, var(--vio-fill) 12%, transparent)',
    bd: 'color-mix(in srgb, var(--vio-fill) 40%, transparent)',
  },
  review: {
    label: 'Review',
    icon: 'ph-book-open',
    ac: 'var(--sky)',
    dim: 'color-mix(in srgb, var(--sky-fill) 12%, transparent)',
    bd: 'color-mix(in srgb, var(--sky-fill) 40%, transparent)',
  },
  manage: {
    label: 'Manage',
    icon: 'ph-wrench',
    ac: 'var(--slate)',
    dim: 'color-mix(in srgb, var(--slate-fill) 12%, transparent)',
    bd: 'color-mix(in srgb, var(--slate-fill) 40%, transparent)',
  },
};
export interface NavItem {
  id: ScreenId;
  icon: string;
  label: string;
  badge?: boolean;
}
export const NAV: Record<'home' | ModeId, NavItem[]> = {
  home: [
    { id: 'dashboard', icon: 'ph-squares-four', label: 'Dashboard' },
    { id: 'widgets', icon: 'ph-grid-nine', label: 'Widget Dashboard' },
  ],
  prepare: [
    { id: 'prepare', icon: 'ph-circle-dashed', label: 'Session Setup' },
    { id: 'riskrules', icon: 'ph-shield', label: 'Risk Rules' },
    { id: 'riskprofile', icon: 'ph-shield-check', label: 'Risk Profile' },
    { id: 'strategies', icon: 'ph-lightning', label: 'Strategies' },
  ],
  execute: [
    { id: 'tradehub', icon: 'ph-arrows-left-right', label: 'TradeHub' },
    { id: 'signals', icon: 'ph-lightning', label: 'AI Signals', badge: true },
    { id: 'trades', icon: 'ph-chart-line', label: 'Trades' },
    { id: 'copytrading', icon: 'ph-copy', label: 'Copy Trading' },
  ],
  analyze: [
    { id: 'analytics', icon: 'ph-chart-bar', label: 'Analytics' },
    { id: 'aiinsights', icon: 'ph-brain', label: 'AI Insights' },
    { id: 'pfscore', icon: 'ph-medal', label: 'PointFive Score', badge: true },
  ],
  review: [
    { id: 'journal', icon: 'ph-book-open', label: 'Journal' },
    { id: 'replay', icon: 'ph-eye', label: 'Trade Replay' },
    { id: 'eod', icon: 'ph-file-text', label: 'End-of-Day Report' },
  ],
  manage: [
    { id: 'accounts', icon: 'ph-wallet', label: 'Accounts' },
    { id: 'notifications', icon: 'ph-bell', label: 'Notifications' },
    { id: 'settings', icon: 'ph-gear-six', label: 'Settings' },
  ],
};
export const RAIL: Record<ModeId, NavItem[]> = {
  prepare: [
    { id: 'briefing', icon: 'ph-newspaper', label: 'News Brief' },
    { id: 'strategies', icon: 'ph-strategy', label: 'Strategies' },
    { id: 'riskrules', icon: 'ph-shield', label: 'Risk Rules' },
  ],
  execute: [
    { id: 'tradehub', icon: 'ph-cursor-click', label: 'TradeHub' },
    { id: 'copytrading', icon: 'ph-copy', label: 'Copy Trading' },
    { id: 'trades', icon: 'ph-chart-line', label: 'Trades' },
  ],
  review: [
    { id: 'replay', icon: 'ph-play-circle', label: 'Trade Replay' },
    { id: 'eod', icon: 'ph-file-text', label: 'End of Day Report' },
    { id: 'acctprogress', icon: 'ph-trend-up', label: 'Account Progress' },
  ],
  analyze: [
    { id: 'analytics', icon: 'ph-chart-bar', label: 'Analytics' },
    { id: 'compareaccts', icon: 'ph-arrows-left-right', label: 'Compare Accounts' },
    { id: 'perfdigest', icon: 'ph-gauge', label: 'Performance Digest' },
    { id: 'pfscore', icon: 'ph-medal', label: 'PointFive Score' },
  ],
  manage: [
    { id: 'accounts', icon: 'ph-wallet', label: 'Accounts' },
    { id: 'investors', icon: 'ph-handshake', label: 'Investors' },
    { id: 'notifications', icon: 'ph-bell', label: 'Notifications' },
    { id: 'settings', icon: 'ph-gear-six', label: 'Settings' },
    { id: 'tradingidentity', icon: 'ph-fingerprint', label: 'Trading Identity' },
    // MOD-BILLING is deliberately absent from this rail. The Baseline lists Billing as a Manage-mode screen, but the
    // design package — which its own §1.2 makes authoritative for screen inventory and visual behaviour — draws the
    // Manage rail with five rows and reaches Billing from the account menu instead (reference line 244). Adding a
    // sixth row moved 507 pixels on every Manage screen, so the package wins and the account menu is the entry
    // point. The screen itself exists at /manage/billing (DEC-026).
  ],
};
export const SCREEN_MODE: Partial<Record<ScreenId, ModeId>> = {
  acctprogress: 'review',
  compareaccts: 'analyze',
  tradingintel: 'analyze',
  perfdigest: 'analyze',
  investors: 'manage',
  tradingidentity: 'manage',
  billing: 'manage',
  briefing: 'prepare',
  prepare: 'prepare',
  riskrules: 'prepare',
  riskprofile: 'prepare',
  strategies: 'prepare',
  tradehub: 'execute',
  signals: 'execute',
  trades: 'execute',
  copytrading: 'execute',
  analytics: 'analyze',
  aiinsights: 'analyze',
  pfscore: 'analyze',
  journal: 'review',
  replay: 'review',
  eod: 'review',
  accounts: 'manage',
  notifications: 'manage',
  settings: 'manage',
};
export const TITLES: Record<ScreenId, string> = {
  billing: 'Billing & Plans',
  acctprogress: 'Account Progress',
  compareaccts: 'Compare Accounts',
  tradingintel: 'Trading Intelligence',
  perfdigest: 'Performance Digest',
  investors: 'Investors',
  tradingidentity: 'Trading Identity',
  dashboard: 'Dashboard',
  widgets: 'Widget Dashboard',
  briefing: 'AI Briefing',
  prepare: 'Prepare',
  riskrules: 'Risk Rules',
  riskprofile: 'Risk Profile',
  strategies: 'Strategies',
  tradehub: 'TradeHub',
  signals: 'AI Signals',
  trades: 'Trades',
  copytrading: 'Copy Trading',
  analytics: 'Analytics',
  aiinsights: 'AI Insights',
  pfscore: 'PointFive Score',
  journal: 'Journal',
  replay: 'Trade Replay',
  eod: 'End-of-Day Report',
  accounts: 'Accounts',
  notifications: 'Notifications',
  settings: 'Settings',
};
